import { useEffect, useState } from "react";
import { listFiles, signUrl, thawStatus, tierFile, type FileRow } from "../api";

export function Files() {
  const [files, setFiles] = useState<FileRow[] | null>(null);
  const [q, setQ] = useState("");
  const [tenant, setTenant] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => { void reload(); }, [q, tenant]);
  async function reload() {
    setErr(null);
    try {
      const r = await listFiles({ q, tenant_id: tenant, limit: "200" });
      setFiles(r.files);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "load failed");
    }
  }

  async function sign(id: number) {
    setBusy(id); setInfo(null); setErr(null);
    try {
      const r = await signUrl(id, 3600);
      await navigator.clipboard.writeText(r.url);
      setInfo(`Signed URL copied to clipboard (expires in 1h).`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "sign failed");
    } finally { setBusy(null); }
  }

  async function checkThaw(id: number) {
    setBusy(id); setInfo(null); setErr(null);
    try {
      const r = await thawStatus(id);
      setInfo(`Backend ${r.backend}${r.ready ? " — READY" : r.thawing ? " — thawing" : " — cold"}${r.thaw_ready_at ? ` (ready by ${r.thaw_ready_at})` : ""}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "thaw status failed");
    } finally { setBusy(null); }
  }

  async function deepArchive(f: FileRow) {
    if (!confirm(
      `Move "${f.filename}" from Wasabi to Glacier Deep Archive?\n\n` +
      `Storage drops to ~AUD $0.0015/GB/mo, but future restores take 12–48h ` +
      `and incur a $0.02/GB retrieval charge.\n\nThis is reversible only by ` +
      `restoring + re-archiving.`
    )) return;
    setBusy(f.id); setInfo(null); setErr(null);
    try {
      const r = await tierFile(f.id);
      setInfo(`File ${r.file_id} tiered to Glacier (${r.bytes_tiered.toLocaleString()} bytes).`);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "tier failed");
    } finally { setBusy(null); }
  }

  return (
    <>
      <div className="row spaced">
        <h2>Archived files</h2>
        <div className="row">
          <input className="input" placeholder="Search filename / path" value={q} onChange={e => setQ(e.target.value)} style={{ width: 280 }} />
          <input className="input" placeholder="Tenant ID" value={tenant} onChange={e => setTenant(e.target.value)} style={{ width: 220 }} />
        </div>
      </div>
      {info && <div className="ok">{info}</div>}
      {err && <div className="err">{err}</div>}
      {!files ? <div>Loading…</div> : (
        <table>
          <thead>
            <tr>
              <th>#</th><th>File</th><th>Path</th><th>Size</th><th>Tier</th><th>Archived</th><th>Last restored</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map(f => (
              <tr key={f.id}>
                <td>{f.id}</td>
                <td>
                  <div>{f.filename}</div>
                  <div className="mono muted">{f.public_id?.slice(0, 8)}…</div>
                </td>
                <td className="mono">{f.original_path}</td>
                <td>{fmtBytes(f.size_bytes)}</td>
                <td><Backend f={f} /></td>
                <td className="mono">{shortTs(f.archived_at)}</td>
                <td className="mono">{f.last_restored_at ? shortTs(f.last_restored_at) : "—"}</td>
                <td>
                  <div className="row">
                    <button className="btn ghost" disabled={busy === f.id} onClick={() => sign(f.id)}>Sign URL</button>
                    {(f.backend ?? "wasabi") === "wasabi" &&
                      <button className="btn ghost" disabled={busy === f.id} onClick={() => deepArchive(f)} title="Move to Glacier Deep Archive (12–48h restore later)">
                        Deep tier
                      </button>}
                    {f.backend === "glacier" &&
                      <button className="btn ghost" disabled={busy === f.id} onClick={() => checkThaw(f.id)}>Thaw status</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function Backend({ f }: { f: FileRow }) {
  if (f.backend === "glacier") {
    const thawing = f.glacier_thaw_started_at && !f.glacier_thaw_ready_at;
    return <span className={"badge " + (thawing ? "purple" : "blue")}>
      {thawing ? "Glacier (thawing)" : "Glacier"}
    </span>;
  }
  return <span className="badge amber">Wasabi</span>;
}

function shortTs(s: string): string { return s.replace("T", " ").replace("Z", "").slice(0, 19); }
function fmtBytes(n: number): string {
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"]; let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return v.toFixed(v >= 100 ? 0 : 1) + " " + units[i];
}
